import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api, wpImg } from "@/lib/api";
import { Loader2, BookOpen, Sparkles, ArrowUpRight, Clock } from "lucide-react";
import MarkdownView from "@/components/MarkdownView";
import FormEmbed from "@/components/FormEmbed";
import ImageLightbox, { ZoomHintOverlay } from "@/components/ImageLightbox";

export default function DynamicPage() {
  const { slug: rawSlug } = useParams();
  // Normalize the slug from the URL: lowercase + trimmed. If the visitor
  // hit /Summer (auto-capitalized link), we redirect to /summer so the
  // canonical URL is clean and shareable.
  const slug = (rawSlug || "").trim().toLowerCase();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true); setNotFound(false); setPage(null);
    api.get(`/pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Redirect canonical URL if visitor's URL had uppercase / trailing spaces.
  if (rawSlug && rawSlug !== slug) {
    return <Navigate to={`/${slug}`} replace />;
  }

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>;
  if (notFound || !page) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6 py-20">
        <div className="text-gold text-xs uppercase tracking-[0.3em] mb-3">404</div>
        <h1 className="font-serif text-4xl text-ink">Page not found</h1>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900">Back home</Link>
      </div>
    );
  }

  // Empty-content detection — show the "Coming soon" landing page when:
  //   • Admin explicitly flagged `coming_soon: true`, OR
  //   • body is empty/whitespace AND no cards have been added yet
  const trimmedBody = (page.body || "").replace(/<[^>]+>/g, "").trim();
  const hasCards = Array.isArray(page.cards) && page.cards.some((c) => c.title || c.body || c.image);
  const showComingSoon = page.coming_soon || (!trimmedBody && !hasCards);

  if (showComingSoon) return <ComingSoonPage page={page} />;

  const readTimeMin = Math.max(1, Math.round((page.body || "").split(/\s+/).length / 220));

  return (
    <div data-testid={`page-${page.slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-sand">
        {page.cover_image && (
          <>
            <div className="absolute inset-0">
              <img src={page.cover_image} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-navy/75 via-navy/80 to-navy/95" />
            </div>
          </>
        )}
        {!page.cover_image && (
          <>
            <div className="absolute inset-0 bg-navy" />
            <div className="absolute inset-0 pattern-girih opacity-25" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(197,160,89,0.22)_0%,rgba(14,42,71,0)_60%)]" />
          </>
        )}

        <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-32 text-cream">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-gold font-semibold">
            <Sparkles size={12} /> {page.parent ? formatParent(page.parent) : "Flint Islamic Center"}
          </div>
          <h1 className="display text-4xl md:text-6xl lg:text-7xl mt-5 leading-[1.05]">{page.title}</h1>
          <div className="mt-6 flex items-center gap-4 text-xs text-cream/70">
            <span className="inline-flex items-center gap-1.5"><BookOpen size={12} className="text-gold" /> {readTimeMin} min read</span>
            {page.parent && (
              <Link to="/" className="inline-flex items-center gap-1.5 hover:text-gold transition-colors">
                More in {formatParent(page.parent)} <ArrowUpRight size={12} />
              </Link>
            )}
          </div>
        </div>

        {/* Decorative wave */}
        <svg className="absolute bottom-0 left-0 right-0 w-full h-8 md:h-12 text-cream" preserveAspectRatio="none" viewBox="0 0 1440 60">
          <path fill="currentColor" d="M0,30 C320,60 1120,0 1440,30 L1440,60 L0,60 Z" />
        </svg>
      </section>

      {/* BODY — markdown content inside a warmer container with drop-cap first paragraph */}
      <section className="relative px-6 md:px-10 py-12 md:py-20">
        <div className="max-w-3xl mx-auto">
          {trimmedBody && (
            <article className="markdown-page">
              <MarkdownView>{page.body}</MarkdownView>
            </article>
          )}

          {/* Visual cards — center-out grid (first card centered, then expands) */}
          {hasCards && <PageCardsGrid cards={page.cards} />}

          {/* CTA buttons */}
          {Array.isArray(page.cta_buttons) && page.cta_buttons.filter((b) => b.label && b.url).length > 0 && (
            <div className="mt-16 space-y-6" data-testid="page-cta-section">
              {page.cta_buttons.filter((b) => b.label && b.url).map((b, i) => <CtaButton key={i} btn={b} idx={i} />)}
            </div>
          )}

          {/* Embedded form (Jotform / Google Forms / Typeform / generic iframe) */}
          {page.embed_form_url && (
            <FormEmbed url={page.embed_form_url} title={page.embed_form_title} />
          )}

          {/* Contact footer — uses per-page contact overrides when set, else
              falls back to the site-wide defaults. */}
          <PageContactFooter page={page} />
        </div>
      </section>
    </div>
  );
}

function PageContactFooter({ page }) {
  const name = (page.contact_name || "").trim();
  const email = (page.contact_email || "").trim();
  const phone = (page.contact_phone || "").trim() || "(810) 406-2617";

  const hasOverride = !!(name || email || (page.contact_phone && page.contact_phone.trim()));

  // CTA target — if a per-page email exists, link straight to mailto:
  // (with a helpful subject). Otherwise fall back to the central /contact form.
  const ctaHref = email
    ? `mailto:${email}?subject=${encodeURIComponent("Inquiry — " + (page.title || "Flint Islamic Center"))}`
    : "/contact";
  const ctaIsExternal = !!email;
  const ctaLabel = email
    ? (name ? `Email ${name.split(" ")[0]}` : "Send Email")
    : "Contact FIC";

  return (
    <div className="mt-16 rounded-3xl bg-navy text-cream p-8 md:p-10 relative overflow-hidden">
      <div className="absolute inset-0 pattern-girih opacity-15" />
      <div className="relative grid md:grid-cols-[1fr,auto] items-center gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Questions?</div>
          <div className="font-serif text-2xl md:text-3xl mt-2">
            {hasOverride && name ? `Reach out to ${name}.` : "We're here to help."}
          </div>
          <div className="text-cream/70 text-sm mt-3 space-y-1">
            <div>
              Call <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="text-cream hover:text-gold transition-colors">{phone}</a>
              {email && <> · or email <a href={`mailto:${email}`} className="text-cream hover:text-gold transition-colors break-all">{email}</a></>}
              {!email && <> or drop us a message.</>}
            </div>
            {name && !email && (
              <div className="text-cream/55 text-xs">Coordinator: {name}</div>
            )}
          </div>
        </div>
        {ctaIsExternal ? (
          <a
            href={ctaHref}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-gold text-navy px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold-600 shadow-lg"
            data-testid="page-contact-cta"
          >
            {ctaLabel} <ArrowUpRight size={12} />
          </a>
        ) : (
          <Link
            to={ctaHref}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-gold text-navy px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold-600 shadow-lg"
            data-testid="page-contact-cta"
          >
            {ctaLabel} <ArrowUpRight size={12} />
          </Link>
        )}
      </div>
    </div>
  );
}

function formatParent(p) {
  return p.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

function CtaButton({ btn, idx }) {
  const [imgFailed, setImgFailed] = useState(false);
  const external = /^https?:\/\//i.test(btn.url);
  const styles = {
    primary: "bg-navy text-cream hover:bg-navy-900 shadow-lg shadow-navy/20",
    gold: "bg-gold text-navy hover:bg-gold-600 shadow-lg shadow-gold/30",
    secondary: "bg-transparent text-navy border-2 border-navy hover:bg-navy hover:text-cream",
  };
  const cls = styles[btn.style] || styles.primary;
  const common = `inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-semibold uppercase tracking-[0.2em] transition-all hover:-translate-y-0.5 ${cls}`;
  const showImage = btn.banner_image && !imgFailed;
  return (
    <div className="rounded-3xl overflow-hidden border border-sand bg-cream" data-testid={`page-cta-${idx}`}>
      {showImage && (
        <img
          src={wpImg(btn.banner_image)}
          alt={btn.label}
          className="w-full h-48 md:h-64 object-cover"
          onError={() => setImgFailed(true)}
        />
      )}
      <div className="p-8 text-center">
        {external ? (
          <a href={btn.url} target="_blank" rel="noreferrer" className={common} data-testid={`page-cta-link-${idx}`}>{btn.label} <ArrowUpRight size={14} /></a>
        ) : (
          <Link to={btn.url} className={common} data-testid={`page-cta-link-${idx}`}>{btn.label} <ArrowUpRight size={14} /></Link>
        )}
      </div>
    </div>
  );
}


/**
 * PageCardsGrid — renders cards in a "center-out" layout. The first card sits
 * dead-center on its row; cards 2 & 3 flank it; 4 & 5 expand further; etc.
 * Visually this gives a symmetric, balanced composition that always feels
 * intentional regardless of card count.
 *
 * Layout strategy:
 *   1 card  → 1 col centered (max-w-md)
 *   2 cards → 2 cols
 *   3 cards → 3 cols on desktop, stacks on mobile (centered card on top)
 *   4-6     → 3 cols
 *   7+      → 3 cols, last row centered
 */
function PageCardsGrid({ cards }) {
  const list = (cards || []).filter((c) => c && (c.title || c.body || c.image));
  if (!list.length) return null;
  // Reorder for center-out: [c0, c1, c2, ...] becomes a sequence where the
  // first card lands in the middle column. We rotate odd/even so each visual
  // row reads from the center outward.
  const reordered = [...list];
  if (list.length === 3) {
    // Move first card to middle column
    reordered[0] = list[1]; reordered[1] = list[0]; reordered[2] = list[2];
  }
  const cols = list.length === 1 ? "grid-cols-1 max-w-md mx-auto"
    : list.length === 2 ? "grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto"
    : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
  return (
    <div className={`mt-12 grid gap-6 ${cols}`} data-testid="page-cards-grid">
      {reordered.map((c, i) => <PageCardTile key={i} card={c} index={i} />)}
    </div>
  );
}

function PageCardTile({ card, index }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const external = card.link_url && /^https?:\/\//i.test(card.link_url);
  const Wrapper = card.link_url
    ? (external
      ? ({ children }) => <a href={card.link_url} target="_blank" rel="noreferrer" className="block">{children}</a>
      : ({ children }) => <Link to={card.link_url} className="block">{children}</Link>)
    : ({ children }) => <div>{children}</div>;
  const showImage = card.image && !imgFailed;
  return (
    <>
      <Wrapper>
        <div
          className={`group relative rounded-3xl overflow-hidden bg-white border border-sand hover:border-gold/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-navy/10 page-card-fade-in`}
          style={{ animationDelay: `${index * 80}ms` }}
          data-testid={`page-card-${index}`}
        >
          {showImage && (
            <button
              type="button"
              onClick={(e) => {
                // Don't follow the card's link_url — open lightbox instead.
                e.preventDefault();
                e.stopPropagation();
                setLightboxOpen(true);
              }}
              aria-label="View full size"
              data-testid={`page-card-image-${index}`}
              className="block w-full aspect-[4/3] overflow-hidden bg-cream relative cursor-zoom-in"
            >
              <img
                src={card.image}
                alt={card.title || ""}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                onError={() => setImgFailed(true)}
              />
              <ZoomHintOverlay />
            </button>
          )}
          <div className="p-6 md:p-7">
            {card.title && <h3 className="font-serif text-xl md:text-2xl text-ink leading-snug">{card.title}</h3>}
            {card.body && (
              <div className="mt-3 text-sm text-ink/70 leading-relaxed line-clamp-5">
                {card.body}
              </div>
            )}
            {card.link_label && card.link_url && (
              <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-gold group-hover:translate-x-1 transition-transform">
                {card.link_label} <ArrowUpRight size={12} />
              </div>
            )}
          </div>
        </div>
      </Wrapper>
      {lightboxOpen && (
        <ImageLightbox
          src={card.image}
          alt={card.title}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}


/**
 * ComingSoonPage — a tasteful placeholder used when an admin creates a page
 * but hasn't filled in the body or any cards yet. Encourages visitors to
 * check back rather than greeting them with a blank screen.
 */
function ComingSoonPage({ page }) {
  return (
    <div className="min-h-[80vh] relative overflow-hidden bg-navy text-cream" data-testid={`page-coming-soon-${page.slug}`}>
      <div className="absolute inset-0 pattern-girih opacity-[0.18]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(197,160,89,0.18)_0%,rgba(14,42,71,0)_60%)]" />
      <div className="relative max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-gold/15 border border-gold/30 px-4 py-1.5 text-[10px] uppercase tracking-[0.32em] text-gold font-bold">
          <Clock size={12} className="animate-pulse" /> Coming soon
        </div>
        <h1 className="display text-5xl md:text-7xl mt-7 leading-[1.05] coming-soon-shimmer">
          {page.title}
        </h1>
        <p className="mt-7 text-cream/75 text-base md:text-lg font-light max-w-xl mx-auto leading-relaxed">
          We're still preparing this page. Beautiful content is on the way —
          please check back soon, or visit the rest of the site to learn about Flint Islamic Center.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold-600 shadow-lg shadow-gold/40 transition-all hover:-translate-y-0.5">
            Back to home <ArrowUpRight size={12} />
          </Link>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-cream/10 border border-cream/20 text-cream px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-cream/15 transition-all">
            Contact us
          </Link>
        </div>
        {/* Decorative ornate dot row */}
        <div className="mt-14 flex items-center justify-center gap-3 text-gold/40">
          <span className="w-1.5 h-1.5 rounded-full bg-gold/40 animate-pulse" />
          <span className="w-1 h-1 rounded-full bg-gold/30 animate-pulse" style={{ animationDelay: "0.3s" }} />
          <span className="w-2 h-2 rounded-full bg-gold/60 animate-pulse" style={{ animationDelay: "0.6s" }} />
          <span className="w-1 h-1 rounded-full bg-gold/30 animate-pulse" style={{ animationDelay: "0.9s" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-gold/40 animate-pulse" style={{ animationDelay: "1.2s" }} />
        </div>
      </div>
    </div>
  );
}
